import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { useAnalyticsOrgLocations } from '../../../../hooks';

export type LocationNSRData = {
  locationId?: string;
  noShowRate?: number;
  diffFromBenchmark?: number;
};

interface LocationBenchmarkDifferenceProps {
  isLowestNSRLocation?: boolean;
  locationNSRData?: LocationNSRData;
}

export const LocationBenchmarkDifference = ({
  isLowestNSRLocation,
  locationNSRData = {},
}: LocationBenchmarkDifferenceProps) => {
  const { t } = useTranslation('analytics');
  const { locationNames } = useAnalyticsOrgLocations({
    module: 'ROI',
  });

  const { iconColor, iconName, message, minIconSize } = useMemo(() => {
    let iconColor: string = theme.colors.critical50;
    let iconName: IconName = 'alert';
    let minIconSize = theme.spacing(3);
    const locationName = locationNames[locationNSRData?.locationId ?? ''] || '';

    if (isLowestNSRLocation) {
      iconColor = theme.colors.success50;
      iconName = 'success';
      minIconSize = theme.spacing(2.25);
    }

    const message = (
      <Trans t={t}>
        <strong>{{ locationName }} </strong> has the {isLowestNSRLocation ? 'lowest' : 'highest'} no-show rate of{' '}
        <strong style={{ color: isLowestNSRLocation ? theme.colors.success50 : theme.colors.critical50 }}>
          {formatters.appendPercent(Math.abs(locationNSRData.noShowRate ?? 0))}
        </strong>
      </Trans>
    );

    return {
      iconColor,
      iconName,
      message,
      minIconSize,
    };
  }, [isLowestNSRLocation, locationNames, locationNSRData, t]);

  if (!locationNSRData?.locationId) {
    return null;
  }

  return (
    <div css={styles.wrapper}>
      <Icon name={iconName} style={{ color: iconColor, minWidth: minIconSize }} />
      <Text>{message}</Text>
    </div>
  );
};

const styles = {
  wrapper: css`
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.medium};
    flex: 1;
    padding: ${theme.spacing(2)};
    display: flex;
    gap: ${theme.spacing(1)};
  `,
};
